import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles, Salad, Coffee, PhoneCall } from "lucide-react";
import { API_BASE, testIds } from "@/lib/api";

const QUICK_ACTIONS = [
  { label: "Free 7-Day Diet Trial", icon: Salad, href: "/diet", testid: "chat-quick-trial" },
  { label: "Buy Coffee", icon: Coffee, href: "/coffee/customize", testid: "chat-quick-coffee" },
  { label: "Talk to Coach", icon: PhoneCall, href: "https://wa.me/919311390365?text=Hi%20Coach%2C%20I%20have%20a%20question", external: true, testid: "chat-quick-coach" },
];

const SESSION_KEY = "coach-sanjay-chat-session";
const GREETING = {
  role: "assistant",
  content:
    "Hi — I'm Coach Sanjay's AI assistant. Ask me anything about nutrition, workouts, mental wellness, or our bootcamps.",
};

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const URL_RE = /(https?:\/\/[^\s<>")\]]+)/g;

function Linkified({ text }) {
  const parts = String(text).split(URL_RE);
  return parts.map((p, i) =>
    URL_RE.test(p) ? (
      <a key={i} href={p} target={p.includes("thejayproject.app") || p.startsWith(window.location.origin) ? "_self" : "_blank"} rel="noreferrer"
        className="underline text-[#AD6A48] hover:text-[#C57B57] break-all" data-testid="chat-link">
        {p.replace(/^https?:\/\//, "")}
      </a>
    ) : (
      p
    )
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);
  const sessionId = useRef(getSessionId()).current;

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });

      if (!res.ok || !res.body) throw new Error("Chat failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  content: (copy[copy.length - 1].content || "") + payload.content,
                };
                return copy;
              });
            } else if (payload.type === "error") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: "Sorry — something went wrong. Please try again.",
                };
                return copy;
              });
            }
          } catch (_) { /* ignore malformed chunk */ }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "I couldn't reach the server. Please try again in a moment.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating toggle */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={testIds.chat.toggle}
        aria-label="Open coach chat"
        className={`fixed z-50 bottom-6 left-6 md:bottom-8 md:left-8 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
          open
            ? "bg-[#89937C] text-white rotate-90"
            : "bg-[#AD6A48] text-white hover:bg-[#C57B57]"
        }`}
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div
          data-testid={testIds.chat.panel}
          className="fixed z-50 bottom-24 left-4 md:bottom-28 md:left-8 w-[calc(100%-2rem)] sm:w-[380px] h-[520px] bg-white border border-[#E5E2D9] rounded-3xl shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="px-5 py-4 border-b border-[#E5E2D9] bg-[#F7F5F0] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-[#AD6A48]/15 flex items-center justify-center">
                <Sparkles size={16} className="text-[#AD6A48]" />
              </div>
              <div>
                <div className="font-serif-display text-lg text-[#2D372B] leading-none">Ask Coach Sanjay</div>
                <div className="text-xs text-[#89937C] mt-1">AI-powered · Nutrition & wellness tips</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid={testIds.chat.close}
              className="text-[#5C665A] hover:text-[#2D372B]"
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={testIds.chat.message(i)}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#89937C] text-white rounded-br-md"
                      : "bg-[#F7F5F0] text-[#2D372B] rounded-bl-md border border-[#E5E2D9]"
                  }`}
                >
                  {m.content ? <Linkified text={m.content} /> : (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          {messages.length <= 1 && !streaming && (
            <div className="px-4 pb-3 flex flex-wrap gap-2" data-testid="chat-quick-actions">
              {QUICK_ACTIONS.map((q) => (
                <a
                  key={q.label}
                  href={q.href}
                  data-testid={q.testid}
                  {...(q.external ? { target: "_blank", rel: "noreferrer" } : {})}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#C57B57]/40 bg-[#C57B57]/8 px-3.5 py-2 text-xs font-medium text-[#AD6A48] hover:bg-[#C57B57] hover:text-white transition-colors"
                >
                  <q.icon size={13} /> {q.label}
                </a>
              ))}
            </div>
          )}

          <div className="border-t border-[#E5E2D9] p-3 flex items-end gap-2 bg-white">
            <textarea
              data-testid={testIds.chat.input}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder="Ask about nutrition, workouts, wellness…"
              className="flex-1 resize-none rounded-2xl border border-[#E5E2D9] px-4 py-2.5 text-sm bg-[#F7F5F0] focus:outline-none focus:border-[#89937C] max-h-28"
            />
            <button
              onClick={send}
              disabled={streaming || !input.trim()}
              data-testid={testIds.chat.send}
              className="h-10 w-10 rounded-full bg-[#89937C] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#737D67] transition-colors"
              aria-label="Send"
            >
              {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
